/* eslint-disable no-continue, no-await-in-loop, max-lines */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "common/utils/api";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";
import type { designLab } from "common/utils/models";
import {
  type AreaOfInterest,
  type Clarification,
  type PinState,
  type TextInfo
} from "../model/pinningState";

type PinInstruction =
  | {
      instruction?: string;
      generativePrompt?: string;
      generativeEligible?: boolean;
      references?: unknown[];
      conflicts?: unknown[];
    }
  | string;

type PriorPinSummary = {
  pin_id: string;
  coordinates: { x: number; y: number };
  state: PinState;
  instruction: PinInstruction;
};

type SubmitPinMessageParams = {
  sessionId: string;
  pinId: string;
  coordinates: { x: number; y: number };
  userMessage: string;
  isFirstMessage: boolean;
  priorPins: PriorPinSummary[];
};

type SseEvent = {
  event: string | undefined;
  data: unknown;
};

type PinResultStatus = "ready" | "needs_clarification" | "unsupported" | "error";

export type AcknowledgmentPayload = {
  message: string;
};

export type ResultPayload = {
  status: PinResultStatus;
  message?: string;
  instruction?: string;
  generativePrompt?: string;
  generativeEligible?: boolean;
  references?: unknown[];
  conflicts?: unknown[];
  clarifications?: Clarification[];
  text?: TextInfo;
  aoiResult?: AreaOfInterest;
};

export type ErrorPayload = {
  error: string;
  status: "error";
};

type PinMessageHandlers = {
  onAcknowledgment: (payload: AcknowledgmentPayload) => void;
  onResult: (payload: ResultPayload) => void;
  onError: (error: Error) => void;
};

function parseSseEventChunk(chunk: string): SseEvent | null {
  const lines = chunk
    .split(/\r?\n/)
    .map(l => l.trimEnd())
    .filter(l => l.length > 0 && !l.startsWith(":"));

  if (lines.length === 0) return null;

  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }

  const dataString = dataLines.join("\n");
  if (!dataString) return null;

  try {
    return { event, data: JSON.parse(dataString) };
  } catch {
    return { event, data: dataString };
  }
}

const MAX_MESSAGE_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

async function attemptPinMessage(
  params: SubmitPinMessageParams,
  handlers: Pick<PinMessageHandlers, "onAcknowledgment" | "onResult">
): Promise<void> {
  const response = await fetch(`/api/pin_sessions/${params.sessionId}/message`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      pin_id: params.pinId,
      coordinates: params.coordinates,
      user_message: params.userMessage,
      is_first_message: params.isFirstMessage,
      prior_pins: params.priorPins.map(p => ({
        pin_id: p.pin_id,
        coordinates: p.coordinates,
        state: p.state,
        instruction:
          typeof p.instruction === "string" ? p.instruction : (p.instruction?.instruction ?? "")
      }))
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to submit pin message (${response.status})`);
  }

  if (!response.body) {
    throw new Error("Missing streaming response body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      const evt = parseSseEventChunk(part);
      if (!evt) continue;

      if (evt.event === "acknowledgment") {
        handlers.onAcknowledgment(objNamesToCamelCase(evt.data) as AcknowledgmentPayload);
      } else if (evt.event === "result") {
        handlers.onResult(objNamesToCamelCase(evt.data) as ResultPayload);
        return;
      } else if (evt.event === "error") {
        const errorPayload = objNamesToCamelCase(evt.data) as ErrorPayload;
        throw new Error(errorPayload?.error || "Server error");
      }
    }
  }
}

async function streamPinMessageFn(params: SubmitPinMessageParams, handlers: PinMessageHandlers) {
  let lastError: Error = new Error("Unknown error");

  for (let attempt = 1; attempt <= MAX_MESSAGE_RETRIES; attempt += 1) {
    try {
      await attemptPinMessage(params, handlers);
      return;
    } catch (e) {
      lastError = e instanceof Error ? e : new Error("Unknown error");

      if (attempt < MAX_MESSAGE_RETRIES) {
        await new Promise(resolve => {
          setTimeout(resolve, RETRY_DELAY_MS * attempt);
        });
      }
    }
  }

  handlers.onError(lastError);
}

type CreatePinningSessionParams = {
  designProjectPlacementId: string;
  artworkId: string;
  previewImageName?: string;
};

type CreatePinningFeedbackParams = {
  sessionId: string;
  feedback: designLab.pinning.createFeedback.Request["feedback"];
};

export function useCreatePinningSession() {
  return useMutation({
    mutationFn: async (params: CreatePinningSessionParams) => {
      const { designProjectPlacementId, previewImageName } = params;
      const requestBody: designLab.pinning.createSession.Request & { previewImageName?: string } = {
        designProjectPlacementId
      };
      if (previewImageName) {
        requestBody["previewImageName"] = previewImageName;
      }
      const data = await api.designLab.pinning.createSession(
        objNamesToSnakeCase(requestBody) as designLab.pinning.createSession.Request
      );
      return objNamesToCamelCase(data);
    }
  });
}

export function useSubmitPinMessage(handlers: PinMessageHandlers) {
  return useMutation({
    mutationFn: async (params: SubmitPinMessageParams) => {
      await streamPinMessageFn(params, handlers);
    }
  });
}

export function useCreatePinningFeedback() {
  return useMutation({
    mutationFn: async (params: CreatePinningFeedbackParams) => {
      const { sessionId, feedback } = params;
      await api.designLab.pinning.createFeedback(
        { id: sessionId },
        objNamesToSnakeCase({
          feedback
        })
      );
    }
  });
}

const POLLING_INTERVAL_MS = 2000;

export function useFetchPins(sessionId: string | null) {
  return useQuery({
    queryKey: ["pins", sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      const response = await api.designLab.pinning.getPins({ id: sessionId });
      return objNamesToCamelCase(response.pins);
    },
    enabled: !!sessionId,
    refetchOnMount: "always",
    refetchInterval: query => {
      const { data } = query.state;
      if (!data || !Array.isArray(data)) return false;
      // Poll when any pin is in processing state to recover from interrupted AI sessions
      const hasProcessingPin = data.some(
        (p: { status?: string; state?: string }) =>
          p.status === "processing" || p.state === "processing"
      );
      return hasProcessingPin ? POLLING_INTERVAL_MS : false;
    }
  });
}

export function useCompletePinningSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const data = await api.designLab.pinning.completeSession(
        objNamesToSnakeCase({ id: sessionId })
      );
      return objNamesToCamelCase(data);
    },
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: ["pins", sessionId] });
    }
  });
}

export function useRemovePin() {
  return useMutation({
    mutationFn: async ({ sessionId, pinId }: { sessionId: string; pinId: string }) =>
      api.designLab.pinning.removePin({ id: sessionId, pinId })
  });
}

export function useCancelPinningSession() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const data = await api.designLab.pinning.cancelSession(
        objNamesToSnakeCase({ id: sessionId })
      );
      return objNamesToCamelCase(data);
    }
  });
}

export function useBulkDeletePins() {
  return useMutation({
    mutationFn: async ({ sessionId, pinIds }: { sessionId: string; pinIds: string[] }) => {
      await api.designLab.pinning.bulkDeletePins(
        { id: sessionId },
        objNamesToSnakeCase({ pinIds })
      );
    }
  });
}

type UpdatePinInstructionParams = {
  sessionId: string;
  pinId: string;
} & designLab.pinning.updatePinInstruction.Request;

export function useUpdatePinInstruction() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      pinId,
      instruction
    }: UpdatePinInstructionParams): Promise<designLab.pinning.updatePinInstruction.Response> => {
      const data = await api.designLab.pinning.updatePinInstruction(
        { id: sessionId, pinId },
        objNamesToSnakeCase({ instruction })
      );
      return objNamesToCamelCase(data);
    }
  });
}
